import Phaser from 'phaser'
import { audio } from './JKGame_audio'
import { DESIGN_HEIGHT, DESIGN_WIDTH } from './JKGame_dims'

// Shared UI tokens + button factory. Keeps Scenes free of inline styling noise
// and gives every interactive control the required visible states.
export const FONT = '"PingFang TC","Noto Sans TC","Microsoft JhengHei",system-ui,sans-serif'

export const COLORS = {
  bg: 0x0b0e14,
  panel: 0x151b26,
  panelEdge: 0x263042,
  zoneSafe: 0x1f6f54,
  zoneForbidden: 0x6e2230,
  zoneNext: 0x7a5a1f,
  zoneStroke: 0x3a4961,
  zoneHere: 0x5ee6a8,
  btn: 0x223049,
  btnHover: 0x30435f,
  btnDanger: 0x5a2231,
  hp: 0xff5a5a,
  sp: 0xffd34d,
  barBg: 0x2a3140,
  text: '#e6e6e6',
  sub: '#9aa4b2',
  good: '#5ee6a8',
  bad: '#ff6b6b',
  system: '#8bb4ff',
  normal: '#cdd5e0',
} as const

// Shared HP-bar colour cue (combat + explore HUDs, so health reads the same everywhere):
// amber only in the 25–50% caution band, the base HP red otherwise. Below 25% the hue
// stays red — the critical state is flagged by the low-HP pulse and the numeric readout,
// not by colour (so reduced-motion + colourblind players still get the value as the cue).
export function hpColor(ratio: number): number {
  if (ratio < 0.25) return 0xff5a5a  // critical — red + pulse + number carry the warning
  if (ratio < 0.5) return 0xffb24d   // caution band — amber tint
  return COLORS.hp                   // healthy — base HP red
}

/** Same HP thresholds as hpColor, as a CSS hex string for Text.setColor. */
export function hpColorHex(ratio: number): string {
  return '#' + hpColor(ratio).toString(16).padStart(6, '0')
}

/** True when the user asked the OS to minimize motion. Callers should skip the most
 *  vestibular-triggering effects (camera shake, big screen-wide motion) when set —
 *  informative cues (numbers, color, fades) stay. Defaults to false off-DOM. */
export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
}

/** True on a device with a precise pointer (mouse/trackpad) — i.e. desktop. Used to
 *  show keyboard-shortcut hints only where keys exist, never on touch. False off-DOM. */
export function hasFinePointer(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: fine)').matches
}

export interface ButtonOpts {
  width: number
  height: number
  label: string
  onTap: () => void
  enabled?: boolean
  fill?: number
  textColor?: string
  fontSize?: number
  /** When disabled, tapping still fires this (e.g. to explain why it's locked). */
  onDisabledTap?: () => void
}

export function makeButton(scene: Phaser.Scene, x: number, y: number, o: ButtonOpts): Phaser.GameObjects.Container {
  const enabled = o.enabled !== false
  const fill = o.fill ?? COLORS.btn
  const container = scene.add.container(x, y)
  const rect = scene.add.rectangle(0, 0, o.width, o.height, fill, enabled ? 1 : 0.4).setStrokeStyle(1, COLORS.zoneStroke)
  const label = scene.add
    .text(0, 0, o.label, { fontFamily: FONT, fontSize: `${o.fontSize ?? 20}px`, color: enabled ? (o.textColor ?? COLORS.text) : COLORS.sub })
    .setOrigin(0.5)
  // Shrink an over-long label to fit so localized text never spills past the edge.
  const maxLabelW = o.width - 20
  if (label.width > maxLabelW) label.setScale(maxLabelW / label.width)
  container.add([rect, label])
  container.setSize(o.width, o.height)
  if (enabled) {
    rect.setInteractive({ useHandCursor: true })
    rect.on('pointerover', () => rect.setFillStyle(COLORS.btnHover, 1))
    rect.on('pointerout', () => {
      rect.setFillStyle(fill, 1)
      container.setScale(1) // restore if the finger slid off before releasing
    })
    // Press feedback: a brief shrink so taps register on touch (no hover there).
    rect.on('pointerdown', () => container.setScale(0.95))
    rect.on('pointerup', () => {
      container.setScale(1)
      audio.play('uiTap') // audible tap feedback (no-op while muted) — fires inside the gesture
      o.onTap()
    })
  } else if (o.onDisabledTap) {
    // Stays visibly disabled, but a tap still explains why (no silent failure).
    rect.setInteractive({ useHandCursor: false })
    rect.on('pointerup', () => o.onDisabledTap!())
  }
  return container
}

export interface ConfirmOpts {
  message: string
  confirmLabel: string
  cancelLabel: string
  onConfirm: () => void
  onCancel: () => void
  confirmFill?: number // accent for the confirm button (e.g. danger for a destructive action)
}

/**
 * A modal confirmation panel: a blocking, tap-outside-to-cancel backdrop + a centred panel
 * with a prompt and confirm/cancel buttons. Shared so every destructive action (gacha spend,
 * abandon-match, …) gets the SAME in-app confirm UX (CLAUDE.md: destructive actions need an
 * in-app panel — never window.confirm). Returns the container; the CALLER tracks it, sets its
 * depth, and destroys it (in the button callbacks + on scene shutdown) — destroying the
 * container tears down the backdrop/panel/text/buttons and their listeners together.
 */
export function makeConfirmPanel(scene: Phaser.Scene, o: ConfirmOpts): Phaser.GameObjects.Container {
  const cx = DESIGN_WIDTH / 2
  const cy = DESIGN_HEIGHT / 2
  const backdrop = scene.add.rectangle(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT, 0x05080d, 0.72).setOrigin(0, 0).setInteractive()
  backdrop.on('pointerup', () => o.onCancel()) // tap outside the panel = cancel
  const panel = scene.add.rectangle(cx, cy, DESIGN_WIDTH - 72, 216, 0x121a26).setStrokeStyle(2, COLORS.panelEdge)
  const msg = scene.add.text(cx, cy - 48, o.message, {
    fontFamily: FONT, fontSize: '19px', color: COLORS.text, align: 'center',
    wordWrap: { width: DESIGN_WIDTH - 120, useAdvancedWrap: true }, lineSpacing: 6,
  }).setOrigin(0.5)
  // Shrink an unusually long localized prompt so it stays within the panel and never grows
  // down into the buttons (button tops sit at cy+22). Centred at cy-48, so the room above the
  // buttons is ~2×(cy+22 − (cy-48)) = ~140px; keep a margin. (Parity with the scenes' fit guards.)
  const maxMsgH = 120
  if (msg.height > maxMsgH) msg.setScale(maxMsgH / msg.height)
  const yes = makeButton(scene, cx - 92, cy + 50, { width: 168, height: 56, label: o.confirmLabel, fontSize: 17, fill: o.confirmFill, onTap: o.onConfirm })
  const no = makeButton(scene, cx + 96, cy + 50, { width: 132, height: 56, label: o.cancelLabel, fontSize: 17, onTap: o.onCancel })
  return scene.add.container(0, 0, [backdrop, panel, msg, yes, no])
}
